import { SKILL_SLOT_MAX } from "@/character/skills/constants";
import {
  GM_SHOP_PLACEHOLDER_IMAGE,
  loadGmShopCatalog,
  type GmShopFulfillmentKind,
} from "@/gm/gm-shop-catalog";
import type { HomeEconomy } from "@/home/economy";
import {
  fulfillmentForTier,
  HEALTH_POTION_HEAL_BY_TIER,
  HEALTH_POTION_LABEL_TH,
  HEALTH_POTION_TIERS,
  healthPotionCount,
  stashItemIdForTier,
} from "@/home/health-potions";
import {
  fulfillmentForManaTier,
  MANA_POTION_LABEL_TH,
  MANA_POTION_RESTORE_BY_TIER,
  MANA_POTION_TIERS,
  manaPotionCount,
  stashItemIdForManaTier,
} from "@/home/mana-potions";
import {
  potionStashTier,
  SHOP_STASH_CARD_ENTRIES,
  type ShopStashItemId,
} from "@/home/shop-definitions";
import { homeIcon } from "@/home/home-icons";

export type StashItemId = ShopStashItemId;

export const STASH_ITEM_DRAG_MIME = "application/x-home-stash-item";

const STASH_ITEM_ID_SET = new Set<string>(
  [
    ...HEALTH_POTION_TIERS.map((tier) => stashItemIdForTier(tier)),
    ...MANA_POTION_TIERS.map((tier) => stashItemIdForManaTier(tier)),
    ...SHOP_STASH_CARD_ENTRIES.map((entry) => entry.stashItemId),
  ],
);

export function isStashItemId(value: string): value is StashItemId {
  return STASH_ITEM_ID_SET.has(value);
}

export type StashSortMode = "name" | "qty-desc" | "qty-asc";

export type StashViewState = {
  sort: StashSortMode;
  detailItemId: StashItemId | null;
  discardQty: number;
};

export function defaultStashViewState(): StashViewState {
  return { sort: "qty-desc", detailItemId: null, discardQty: 1 };
}

export function cycleStashSortMode(sort: StashSortMode): StashSortMode {
  if (sort === "qty-desc") return "qty-asc";
  if (sort === "qty-asc") return "name";
  return "qty-desc";
}

export function stashSortLabel(sort: StashSortMode): string {
  if (sort === "name") return "ชื่อ";
  if (sort === "qty-asc") return "จำนวน ↑";
  return "จำนวน ↓";
}

type StashSlot = {
  id: StashItemId;
  name: string;
  hint: string;
  count: number;
  imageDataUrl: string;
  shopTab: "coins" | "gems" | "cash";
  tier: "common" | "rare" | "epic";
};

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function imageForFulfillment(kind: GmShopFulfillmentKind): string {
  const item = loadGmShopCatalog().find((entry) => entry.fulfillment === kind);
  return item?.imageDataUrl ?? GM_SHOP_PLACEHOLDER_IMAGE;
}

export function buildStashSlots(economy: HomeEconomy): StashSlot[] {
  const potions: StashSlot[] = [
    ...HEALTH_POTION_TIERS.map((tier) => ({
      id: stashItemIdForTier(tier),
      name: HEALTH_POTION_LABEL_TH[tier],
      hint: `ฟื้น +${HEALTH_POTION_HEAL_BY_TIER[tier]} HP`,
      count: healthPotionCount(economy, tier),
      imageDataUrl: imageForFulfillment(fulfillmentForTier(tier)),
      shopTab: "coins" as const,
      tier: potionStashTier(tier),
    })),
    ...MANA_POTION_TIERS.map((tier) => ({
      id: stashItemIdForManaTier(tier),
      name: MANA_POTION_LABEL_TH[tier],
      hint: `ฟื้น +${MANA_POTION_RESTORE_BY_TIER[tier]} มานา`,
      count: manaPotionCount(economy, tier),
      imageDataUrl: imageForFulfillment(fulfillmentForManaTier(tier)),
      shopTab: "coins" as const,
      tier: potionStashTier(tier),
    })),
  ];
  const cardCounts: Record<string, number> = {
    revive_card: economy.wallet.reviveCards,
    skill_cooldown_card: economy.skillCooldownCards,
    skill_ap_card: economy.skillApCards,
    skill_buff_duration_card: economy.skillBuffDurationCards,
    skill_aoe_range_card: economy.skillAoeRangeCards,
    skill_create_card: economy.skillCreateCards,
    shadow_create_card: economy.shadowCreateCards,
    shadow_ap_card: economy.shadowApCards,
  };
  const cards: StashSlot[] = SHOP_STASH_CARD_ENTRIES.map((entry) => ({
    id: entry.stashItemId,
    name: entry.name,
    hint: entry.hint,
    count: cardCounts[entry.stashItemId] ?? 0,
    imageDataUrl: imageForFulfillment(entry.fulfillment),
    shopTab: entry.shopTab,
    tier: entry.tier,
  }));
  return [...potions, ...cards];
}

function sortSlots(slots: StashSlot[], sort: StashSortMode): StashSlot[] {
  const list = [...slots];
  if (sort === "name") {
    list.sort((a, b) => a.name.localeCompare(b.name, "th"));
    return list;
  }
  if (sort === "qty-asc") {
    list.sort((a, b) => a.count - b.count || a.name.localeCompare(b.name, "th"));
    return list;
  }
  list.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "th"));
  return list;
}

function renderStashSlot(slot: StashSlot): string {
  const empty = slot.count <= 0 ? " home-stash-slot--empty" : "";
  return `
    <button type="button" class="home-stash-slot home-stash-slot--${slot.tier}${empty}" data-stash-open-item="${escapeHtml(slot.id)}" data-stash-drag-item="${escapeHtml(slot.id)}" draggable="true" aria-label="${escapeHtml(slot.name)} ${slot.count}">
      <span class="home-stash-slot-frame">
        <img class="home-stash-slot-img" src="${escapeHtml(slot.imageDataUrl)}" alt="" width="64" height="64" loading="lazy" />
        <span class="home-stash-slot-qty">${slot.count.toLocaleString("en-US")}</span>
      </span>
      <span class="home-stash-slot-name">${escapeHtml(slot.name)}</span>
    </button>`;
}

function renderStashToolbar(economy: HomeEconomy, sort: StashSortMode): string {
  return `
    <div class="home-stash-toolbar">
      <button type="button" class="home-stash-tool-btn" data-stash-sort-cycle aria-label="เปลี่ยนการเรียง">
        เรียง · ${escapeHtml(stashSortLabel(sort))}
      </button>
      <p class="home-stash-toolbar-meta hint">ช่องสกิล ${economy.skillSlotsUnlocked}/${SKILL_SLOT_MAX}</p>
    </div>
    <p class="hint home-stash-lede">ลากไอเทมลงแถบ 3 ช่องด้านล่าง · ลากออกนอกแถบเพื่อเอาออก · กดช่องดูรายละเอียด</p>`;
}

export function renderStashModalBody(economy: HomeEconomy, view: StashViewState): string {
  const slots = sortSlots(buildStashSlots(economy), view.sort);
  return `
    ${renderStashToolbar(economy, view.sort)}
    <div class="home-stash-grid" role="list">
      ${slots.map(renderStashSlot).join("")}
    </div>`;
}

export function renderStashDetailBody(
  economy: HomeEconomy,
  itemId: StashItemId,
  discardQty: number,
): string {
  const slot = buildStashSlots(economy).find((entry) => entry.id === itemId);
  if (!slot) return "";
  const maxQty = Math.max(0, slot.count);
  const qty = maxQty > 0 ? Math.min(Math.max(1, discardQty), maxQty) : 1;
  return `
    <div class="home-stash-detail-layout">
      <div class="home-stash-detail-art home-stash-slot--${slot.tier}">
        <img src="${escapeHtml(slot.imageDataUrl)}" alt="" width="120" height="120" />
        <span class="home-stash-slot-qty home-stash-slot-qty--lg">${slot.count.toLocaleString("en-US")}</span>
      </div>
      <h3 class="home-stash-detail-title">${escapeHtml(slot.name)}</h3>
      <p class="hint">${escapeHtml(slot.hint)}</p>
      <p class="hint">มีในคลัง <strong>${slot.count.toLocaleString("en-US")}</strong> ชิ้น</p>
      ${
        maxQty > 0
          ? `<div class="home-shop-qty-row" role="group" aria-label="จำนวนที่ทิ้ง">
        <span class="field-label">ทิ้งจำนวน</span>
        <div class="home-shop-qty-controls">
          <button type="button" class="home-shop-qty-btn" data-stash-discard-minus aria-label="ลดจำนวน">−</button>
          <input type="number" class="text-input home-shop-qty-input" id="home-stash-discard-qty" min="1" max="${maxQty}" value="${qty}" inputmode="numeric" />
          <button type="button" class="home-shop-qty-btn" data-stash-discard-plus aria-label="เพิ่มจำนวน">+</button>
        </div>
      </div>
      <button type="button" class="btn btn-ghost home-stash-discard-btn" data-stash-discard-confirm="${escapeHtml(itemId)}">ทิ้ง</button>`
          : `<p class="hint">ไม่มีในคลัง</p>`
      }
      ${
        itemId === "shadow_ap_card" && maxQty > 0
          ? `<button type="button" class="btn btn-primary" data-stash-use-shadow-ap>ใช้ที่ร่างเงา</button>`
          : ""
      }
      ${
        itemId === "shadow_create_card" && maxQty > 0
          ? `<button type="button" class="btn btn-ghost" data-stash-open-shadow-tab>ไปแท็บร่างเงา</button>`
          : ""
      }
      <button type="button" class="text-link home-stash-shop-link" data-stash-goto-shop="${slot.shopTab}">ซื้อที่ร้าน</button>
    </div>`;
}

export function stashModalMarkup(): string {
  return `
    <div id="home-stash-modal" class="home-cash-shop-modal home-stash-modal" hidden aria-hidden="true">
      <button type="button" class="home-cash-shop-backdrop" data-close-stash aria-label="ปิดคลัง"></button>
      <div class="home-cash-shop-dialog home-stash-dialog" role="dialog" aria-modal="true" aria-labelledby="home-stash-title">
        <header class="home-stash-head">
          <button type="button" class="btn btn-ghost btn-icon" data-close-stash aria-label="ปิด">
            ${homeIcon("close")}
          </button>
          <h2 id="home-stash-title" class="home-stash-head-title">คลัง</h2>
          <span class="home-stash-head-spacer" aria-hidden="true"></span>
        </header>
        <div id="home-stash-body" class="home-stash-body"></div>
      </div>
      <div id="home-stash-detail-modal" class="home-shop-detail-modal home-stash-detail-modal" hidden aria-hidden="true">
        <button type="button" class="home-shop-detail-backdrop" data-close-stash-detail aria-label="ปิดรายละเอียด"></button>
        <div class="home-shop-detail-dialog home-stash-detail-dialog" role="dialog" aria-modal="true" aria-labelledby="home-stash-detail-title">
          <header class="home-shop-detail-head">
            <button type="button" class="btn btn-ghost btn-icon home-shop-detail-back" data-close-stash-detail aria-label="กลับ">
              ${homeIcon("back")}
            </button>
            <h2 id="home-stash-detail-title" class="home-shop-detail-head-title">รายละเอียด</h2>
            <button type="button" class="btn btn-ghost btn-icon" data-close-stash-detail aria-label="ปิด">
              ${homeIcon("close")}
            </button>
          </header>
          <div id="home-stash-detail-content" class="home-stash-detail-content"></div>
        </div>
      </div>
    </div>`;
}

export function refreshStashModalDom(
  root: ParentNode,
  economy: HomeEconomy,
  view: StashViewState,
): void {
  const body = root.querySelector<HTMLElement>("#home-stash-body");
  if (body) body.innerHTML = renderStashModalBody(economy, view);

  const detailModal = root.querySelector<HTMLElement>("#home-stash-detail-modal");
  const detailContent = root.querySelector<HTMLElement>("#home-stash-detail-content");
  if (!detailModal || !detailContent) return;

  if (!view.detailItemId) {
    detailModal.hidden = true;
    detailModal.setAttribute("aria-hidden", "true");
    return;
  }

  detailContent.innerHTML = renderStashDetailBody(economy, view.detailItemId, view.discardQty);
  detailModal.hidden = false;
  detailModal.setAttribute("aria-hidden", "false");
}

export function getStashSlotById(
  economy: HomeEconomy,
  itemId: StashItemId,
): StashSlot | undefined {
  return buildStashSlots(economy).find((entry) => entry.id === itemId);
}
